<template>
  <section class="admin-content" id="payout-accounts">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 32px 0; gap: 12px;">
            <div class="header-icon-wrap"><i class="fe fe-credit-card" style="font-size: 22px;"></i></div>
            <div class="flex-grow-1">
              <h4 class="mb-0 font-weight-bold">Payout account</h4>
              <small style="opacity: 0.8;">{{ facility ? facility.agency_name + " · " : "" }}Where capitation, claims payments and approved loans are sent</small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="loading" class="card shadow-sm"><div class="card-body empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div></div>
          <template v-else>
            <div class="card shadow-sm table-card mb-4">
              <div class="form-card-header">
                <div class="section-number"><i class="fe fe-list" style="font-size: 12px;"></i></div>
                <h6 class="mb-0 font-weight-bold text-white">Your accounts</h6>
              </div>
              <div class="card-body">
                <div v-if="!accounts.length" class="alert alert-warning small mb-0">
                  <i class="fe fe-alert-triangle mr-1"></i> No payout account yet. Add a bank account or open a wallet below, or the agency cannot pay you online.
                </div>
                <div v-for="a in accounts" :key="a.id" class="acct" :class="{ default: a.is_default }">
                  <div class="acct-icon"><i class="fe" :class="a.kind === 'wallet' ? 'fe-briefcase' : 'fe-home'"></i></div>
                  <div class="flex-grow-1">
                    <div class="font-weight-bold">{{ a.account_name || "—" }}</div>
                    <div class="small text-muted">{{ a.bank_name || a.bank_code }} · {{ a.account_number }} · {{ a.kind === "wallet" ? "Safehaven wallet" : "Bank account" }}</div>
                    <div v-if="a.kind === 'wallet' && balances[a.id] !== undefined" class="small mt-1">Balance: <b>₦{{ fmt(balances[a.id]) }}</b></div>
                  </div>
                  <div class="d-flex flex-wrap align-items-center" style="gap: 6px;">
                    <span v-if="a.is_default" class="badge badge-soft-success">Payments go here</span>
                    <button v-else class="btn btn-sm btn-outline-primary" :disabled="busy" @click="makeDefault(a)">Use for payments</button>
                    <button v-if="a.kind === 'wallet'" class="btn btn-sm btn-outline-secondary" @click="balance(a)"><i class="fe fe-refresh-cw"></i> Balance</button>
                    <button class="btn btn-sm btn-outline-danger" :disabled="busy" @click="remove(a)"><i class="fe fe-trash-2"></i></button>
                  </div>
                </div>
                <small class="text-muted d-block mt-2">Changing the account stops any payment already prepared to the old one; the agency then prepares it again. Every change is recorded.</small>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-6 mb-4">
                <div class="card shadow-sm table-card h-100">
                  <div class="form-card-header">
                    <div class="section-number"><i class="fe fe-home" style="font-size: 12px;"></i></div>
                    <h6 class="mb-0 font-weight-bold text-white">Add a bank account</h6>
                  </div>
                  <div class="card-body">
                    <div class="form-group">
                      <label class="small font-weight-bold text-secondary">Bank</label>
                      <v-select v-model="bank.code" :options="banks" label="name" :reduce="(b) => b.code" placeholder="Search bank…" :loading="banksLoading" />
                    </div>
                    <div class="form-group">
                      <label class="small font-weight-bold text-secondary">Account number (10 digits)</label>
                      <input class="form-control" v-model.trim="bank.account" inputmode="numeric" maxlength="10" @input="bank.account = bank.account.replace(/\D/g, '')" />
                    </div>
                    <button class="btn btn-submit" :disabled="busy || !bank.code || bank.account.length !== 10" @click="addBank">
                      <span v-if="busy === 'bank'" class="spinner-border spinner-border-sm mr-1"></span> Confirm with the bank and save
                    </button>
                    <small class="d-block text-muted mt-2">The bank confirms the account name before it is saved.</small>
                  </div>
                </div>
              </div>
              <div class="col-lg-6 mb-4">
                <div class="card shadow-sm table-card h-100">
                  <div class="form-card-header">
                    <div class="section-number"><i class="fe fe-briefcase" style="font-size: 12px;"></i></div>
                    <h6 class="mb-0 font-weight-bold text-white">Or open a Safehaven wallet</h6>
                  </div>
                  <div class="card-body">
                    <template v-if="wallet.step === 1">
                      <p class="small text-muted">A corporate account for the facility at Safe Haven MFB. A director's BVN or NIN is verified with a code sent to their phone.</p>
                      <div class="form-row">
                        <div class="form-group col-4">
                          <label class="small font-weight-bold text-secondary">ID type</label>
                          <select class="form-control" v-model="wallet.id_type"><option>BVN</option><option>NIN</option></select>
                        </div>
                        <div class="form-group col-8">
                          <label class="small font-weight-bold text-secondary">Director's {{ wallet.id_type }}</label>
                          <input class="form-control" v-model.trim="wallet.id_number" inputmode="numeric" maxlength="11" @input="wallet.id_number = wallet.id_number.replace(/\D/g, '')" />
                        </div>
                      </div>
                      <button class="btn btn-submit" :disabled="busy || wallet.id_number.length !== 11" @click="startWallet">
                        <span v-if="busy === 'wallet'" class="spinner-border spinner-border-sm mr-1"></span> Send code
                      </button>
                    </template>
                    <template v-else>
                      <div class="alert alert-info small py-2">{{ wallet.message }}</div>
                      <div class="form-row">
                        <div class="form-group col-md-4"><label class="small font-weight-bold text-secondary">Code</label><input class="form-control" v-model.trim="wallet.otp" maxlength="8" /></div>
                        <div class="form-group col-md-8"><label class="small font-weight-bold text-secondary">CAC RC / BN number</label><input class="form-control" v-model.trim="wallet.rc_number" /></div>
                        <div class="form-group col-md-6"><label class="small font-weight-bold text-secondary">Email</label><input type="email" class="form-control" v-model.trim="wallet.email" /></div>
                        <div class="form-group col-md-6"><label class="small font-weight-bold text-secondary">Phone</label><input v-phone class="form-control" v-model="wallet.phone_number" /></div>
                      </div>
                      <div class="d-flex" style="gap: 8px;">
                        <button class="btn btn-submit" :disabled="busy || !wallet.otp || !wallet.rc_number || !wallet.email || (wallet.phone_number || '').length !== 11" @click="confirmWallet">
                          <span v-if="busy === 'wallet'" class="spinner-border spinner-border-sm mr-1"></span> Open wallet
                        </button>
                        <button class="btn btn-outline-secondary" @click="wallet.step = 1">Back</button>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";

/**
 * A facility's payout accounts (payout/accounts…). Facility users manage their own; agency finance staff open it with
 * ?provider_id= to manage a facility's account on its behalf.
 */
export default {
  components: { Navbar },
  data() {
    return {
      accounts: [], facility: null, banks: [], banksLoading: false, loading: true, busy: false, balances: {},
      bank: { code: null, account: "" },
      wallet: { step: 1, id_type: "BVN", id_number: "", otp: "", rc_number: "", email: "", phone_number: "", message: "" },
    };
  },
  computed: {
    params() { return this.$route.query.provider_id ? { provider_id: this.$route.query.provider_id } : {}; },
  },
  created() {
    this.load();
    this.banksLoading = true;
    this.axios.get("/api/v1/auth/payout/banks").then((r) => { this.banks = r.data.data; }).catch(() => {}).finally(() => { this.banksLoading = false; });
  },
  methods: {
    fmt(v) { return Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    msg(e, f) { const d = e.response && e.response.data; return (d && (d.errors ? Object.values(d.errors).flat()[0] : d.message)) || f; },
    ok(m) { this.$toasted.success(m, { position: "top-center", duration: 5000 }); },
    bad(e, f) { this.$toasted.error(this.msg(e, f), { position: "top-center", duration: 6000 }); },
    load() {
      this.axios.get("/api/v1/auth/payout/accounts", { params: this.params })
        .then((r) => {
          this.accounts = r.data.data;
          this.facility = r.data.facility;
          if (this.facility && !this.wallet.email) { this.wallet.email = this.facility.email || ""; this.wallet.phone_number = this.facility.phone_number || ""; }
        })
        .catch((e) => this.bad(e, "Could not load the accounts."))
        .finally(() => { this.loading = false; });
    },
    addBank() {
      this.busy = "bank";
      this.axios.post("/api/v1/auth/payout/accounts/bank", { ...this.params, bank_code: this.bank.code, account_number: this.bank.account, make_default: true })
        .then((r) => { this.ok(r.data.message); this.bank = { code: null, account: "" }; this.load(); })
        .catch((e) => this.bad(e, "The account could not be saved."))
        .finally(() => { this.busy = false; });
    },
    startWallet() {
      this.busy = "wallet";
      this.axios.post("/api/v1/auth/payout/accounts/wallet/start", { ...this.params, id_type: this.wallet.id_type, id_number: this.wallet.id_number })
        .then((r) => { this.wallet.message = r.data.message; this.wallet.step = 2; })
        .catch((e) => this.bad(e, "The verification could not be started."))
        .finally(() => { this.busy = false; });
    },
    confirmWallet() {
      this.busy = "wallet";
      const w = this.wallet;
      this.axios.post("/api/v1/auth/payout/accounts/wallet/confirm", { ...this.params, otp: w.otp, rc_number: w.rc_number, email: w.email, phone_number: w.phone_number })
        .then((r) => { this.ok(r.data.message); this.wallet = { ...this.wallet, step: 1, id_number: "", otp: "" }; this.load(); })
        .catch((e) => this.bad(e, "The wallet could not be opened."))
        .finally(() => { this.busy = false; });
    },
    makeDefault(a) {
      if (!confirm(`Send all future payments to ${a.account_name} (${a.account_number})?`)) return;
      this.busy = true;
      this.axios.post(`/api/v1/auth/payout/accounts/${a.id}/default`, this.params).then((r) => { this.ok(r.data.message); this.load(); })
        .catch((e) => this.bad(e, "Could not change the account.")).finally(() => { this.busy = false; });
    },
    remove(a) {
      if (!confirm(`Remove ${a.account_name} (${a.account_number})?`)) return;
      this.busy = true;
      this.axios.delete(`/api/v1/auth/payout/accounts/${a.id}`, { params: this.params }).then((r) => { this.ok(r.data.message); this.load(); })
        .catch((e) => this.bad(e, "Could not remove the account.")).finally(() => { this.busy = false; });
    },
    balance(a) {
      this.axios.get(`/api/v1/auth/payout/accounts/${a.id}/balance`, { params: this.params })
        .then((r) => { this.$set(this.balances, a.id, r.data.data.balance); })
        .catch((e) => this.bad(e, "Could not read the balance."));
    },
  },
};
</script>

<style scoped>
@import "../reports/reports.css";
.acct { display: flex; align-items: center; gap: 12px; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; margin-bottom: 10px; flex-wrap: wrap; }
.acct.default { border-color: #86efac; background: #f0fdf4; }
.acct-icon { width: 40px; height: 40px; border-radius: 10px; background: #eff6ff; color: #2563eb; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; }
</style>
